import * as React from 'react';

import {Button} from 'shells/button/button';
import {Popover} from 'shells/popover/popover';
import {Body} from 'shells/typography/body';
import {useRefObserver} from 'shells/utils/hooks/use-ref-observer';

import type {ExternalService} from 'nutshell-graphql-types';
import type {GetActivityForModal_activity as Activity} from 'nutshell-graphql-types';

import {getFooterTimestamp, getCreatorOrigin} from '../helpers';

import {SyncStatusSection} from './sync-status-section';

type Props = {
    activity: Activity | null | undefined;
    onCancel: () => void;
    showSyncStatus: boolean;
    isSyncing: boolean;
    handleUpdateActivity: () => void;
    isConnectedToExternalCalendar: boolean;
    calendarSyncExternalService: ExternalService | null | undefined;
    isHidden: boolean;
};

export function LeftSection(props: Props) {
    const loggedByRef = React.useRef(null);
    const {isHovered} = useRefObserver(loggedByRef, ['isHovered']);

    if (props.isHidden && props.activity) {
        return <div></div>;
    }

    const syncStatus = (
        <SyncStatusSection
            isSyncing={props.isSyncing}
            handleUpdateActivity={props.handleUpdateActivity}
            isConnectedToExternalCalendar={props.isConnectedToExternalCalendar}
            calendarSyncExternalService={props.calendarSyncExternalService}
            isScheduler={Boolean(props.activity && props.activity.hasBooking)}
            syncMode={props.activity && props.activity.syncMode}
            serviceType={
                props.activity &&
                props.activity.externalMeeting &&
                props.activity.externalMeeting.serviceType
            }
        />
    );

    return (
        <>
            {props.activity ? (
                <>
                    {(props.activity.status === 'SCHEDULED' ||
                        props.activity.status === 'OVERDUE') && (
                        <div>
                            <Body color='light' truncate={{type: 'inline'}} size='small'>
                                Created{' '}
                                {props.activity.creatorUser && (
                                    <>
                                        by{' '}
                                        <Body isBold={true} color='light' size='small'>
                                            {props.activity.creatorUser.name}
                                        </Body>{' '}
                                    </>
                                )}
                                on {getFooterTimestamp(props.activity.createdTime)}
                                {props.activity && getCreatorOrigin(props.activity)}
                            </Body>
                            {props.showSyncStatus && syncStatus}
                        </div>
                    )}
                    {props.activity &&
                        props.activity.status === 'LOGGED' &&
                        props.activity.loggedTime && (
                            <div>
                                <div ref={loggedByRef}>
                                    <Body
                                        color='light'
                                        as='div'
                                        truncate={{type: 'inline'}}
                                        size='small'
                                    >
                                        Logged{' '}
                                        {props.activity.autologged ? (
                                            <>automatically </>
                                        ) : (
                                            props.activity.loggedBy && (
                                                <>
                                                    by{' '}
                                                    <Body isBold={true} color='light' size='small'>
                                                        {props.activity.loggedBy.name}
                                                    </Body>{' '}
                                                </>
                                            )
                                        )}
                                        on {getFooterTimestamp(props.activity.loggedTime)}
                                    </Body>
                                </div>
                                {isHovered && loggedByRef.current && (
                                    <Popover
                                        anchor={loggedByRef.current}
                                        location='top'
                                        isNubDark={true}
                                        bodyColor='dark'
                                    >
                                        <div className='pad-8'>
                                            Created{' '}
                                            {props.activity &&
                                                props.activity.creatorUser &&
                                                `by ${props.activity.creatorUser.name}`}
                                            {props.activity &&
                                                getCreatorOrigin(props.activity, true)}
                                        </div>
                                    </Popover>
                                )}
                                {props.showSyncStatus && syncStatus}
                            </div>
                        )}
                </>
            ) : (
                <Button size='big' variant='text-secondary' onClick={props.onCancel}>
                    Cancel
                </Button>
            )}
        </>
    );
}
